import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { CircularProgress, Paper, Typography, Box, Avatar, Button } from "@material-ui/core";
import { makeStyles, createTheme, ThemeProvider } from "@material-ui/core/styles";
import { 
  LocationOn, 
  Phone, 
  Person,
  ErrorOutline,
  Room as RoomIcon
} from "@material-ui/icons";
import api from "../../services/api";
import { useTitle } from "../../hooks/useTitle";

import ScheduleView from "./components/ScheduleView";
import RescheduleView from "./components/RescheduleView";
import HistoryView from "./components/HistoryView";

const lightTheme = createTheme({
  palette: {
    type: "light",
    primary: { main: "#1976d2" },
    secondary: { main: "#dc004e" },
    background: {
      default: "#f4f6f8",
      paper: "#ffffff"
    },
    text: {
        primary: "rgba(0, 0, 0, 0.87)",
        secondary: "rgba(0, 0, 0, 0.54)"
    }
  },
});

const useStyles = makeStyles((theme) => ({
  root: {
    minHeight: "100vh",
    backgroundColor: "#f4f6f8",
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
  },
  headerPaper: {
    width: "100%",
    maxWidth: 900,
    marginBottom: theme.spacing(3),
    borderRadius: 16,
    overflow: "hidden",
    position: "relative",
    background: "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
    border: "1px solid rgba(0,0,0,0.08)",
    boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
  },
  headerBanner: {
    height: 8,
    background: "linear-gradient(90deg, #1976d2 0%, #dc004e 100%)",
    width: "100%",
  },
  headerContent: {
    padding: theme.spacing(3, 4),
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    [theme.breakpoints.down("xs")]: {
      flexDirection: "column",
      alignItems: "flex-start",
      gap: theme.spacing(2),
      padding: theme.spacing(3),
    }
  },
  logoContainer: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2.5),
  },
  logo: {
    width: 72,
    height: 72,
    borderRadius: 12,
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
    border: "2px solid #fff",
    objectFit: "contain",
    backgroundColor: "#fff"
  },
  companyInfo: {
    display: "flex",
    flexDirection: "column",
  },
  companyName: {
    fontWeight: 800,
    fontSize: "1.5rem",
    lineHeight: 1.2,
    color: "#333",
    letterSpacing: "-0.5px",
  },
  contactRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    marginTop: theme.spacing(0.5),
    color: "#666",
    flexWrap: "wrap",
  },
  contactItem: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    fontSize: "0.85rem",
    fontWeight: 500,
  },
  patientBadge: {
    padding: "8px 16px",
    backgroundColor: "rgba(33, 150, 243, 0.08)", 
    borderRadius: 50,
    border: "1px solid rgba(33, 150, 243, 0.2)",
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  iconSmall: {
    fontSize: 16,
    opacity: 0.7,
  },
  contentPaper: {
    width: "100%",
    maxWidth: 900,
    padding: theme.spacing(3),
    minHeight: 400,
    borderRadius: 12,
    [theme.breakpoints.down("xs")]: {
        padding: theme.spacing(2),
    }
  },
  errorBox: {
    textAlign: "center",
    padding: theme.spacing(4),
    color: "#d32f2f",
  },
  mapsButton: {
    textTransform: "none",
    fontSize: "0.8rem",
    fontWeight: 600,
    borderRadius: 20,
    padding: "2px 10px",
    [theme.breakpoints.down("xs")]: {
      fontSize: "0.75rem",
      padding: "2px 8px",
    },
  },
}));

const PublicAccess = () => {
  useTitle("Acceso Paciente");
  const classes = useStyles(); 
  const location = useLocation();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sessionData, setSessionData] = useState(null);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const token = queryParams.get("token");

    if (!token) {
      setError("Token de acceso no proporcionado.");
      setLoading(false);
      return;
    }

    const validateToken = async () => {
      try {
        const { data } = await api.get("/link", {
          params: { token }
        });
        setSessionData(data);
      } catch (err) {
        console.error(err);
        const msg = err.response?.data?.error || "El enlace es inválido o ha expirado.";
        setError(msg);
      } finally {
        setLoading(false);
      }
    };

    validateToken();
  }, [location.search]);

  const renderContent = () => {
    if (loading) {
        return (
          <div className={classes.root} style={{ justifyContent: "center" }}>
            <CircularProgress size={60} />
            <Typography variant="h6" style={{ marginTop: 20, color: "#666" }}>
              Verificando acceso...
            </Typography>
          </div>
        );
      }
    
      if (error) {
        return (
          <div className={classes.root}>
            <Paper className={classes.contentPaper} style={{ maxWidth: 500, marginTop: 100 }}>
              <Box className={classes.errorBox}>
                <ErrorOutline style={{ fontSize: 80, marginBottom: 16 }} />
                <Typography variant="h5" gutterBottom>Acceso Denegado</Typography>
                <Typography variant="body1">{error}</Typography>
              </Box>
            </Paper>
          </div>
        );
      }
    
      const { type, company, patientName } = sessionData;
    
      return (
        <div className={classes.root}>
          <Paper className={classes.headerPaper} elevation={0}>
            <div className={classes.headerBanner} />

            <div className={classes.headerContent}>
              <div className={classes.logoContainer}>
                {company?.logoUrl ? (
                  <Avatar src={company.logoUrl} className={classes.logo} variant="rounded" />
                ) : (
                  <Avatar className={classes.logo} variant="rounded">
                    {company?.name?.charAt(0)}
                  </Avatar>
                )}

                <div className={classes.companyInfo}>
                  <Typography className={classes.companyName}>
                    {company?.name || "Clínica Médica"}
                  </Typography>

                  <div className={classes.contactRow}>
                    {company?.city && (
                      <div className={classes.contactItem}>
                        <LocationOn className={classes.iconSmall} />
                        {company.city}, {company.country}
                      </div>
                    )}
                    {company?.phone && (
                      <div className={classes.contactItem}>
                        <Phone className={classes.iconSmall} />
                        {company.phone}
                      </div>
                    )}

                    {company && company.latitude !== 0 && company.longitude !== 0 && (
                      <Button
                        variant="outlined"
                        color="primary"
                        size="small"
                        className={classes.mapsButton}
                        href={"https://www.google.com/maps?q=" + company.latitude + "," + company.longitude}
                        target="_blank"
                        rel="noopener noreferrer"
                        startIcon={<RoomIcon />}
                      >
                        Maps
                      </Button>
                    )}
                  </div>
                </div>
              </div>

              <div className={classes.patientBadge}>
                <Avatar style={{ width: 24, height: 24, fontSize: 12, backgroundColor: "#1976d2" }}>
                  <Person style={{ fontSize: 16 }} />
                </Avatar>
                <Typography variant="body2" color="primary" style={{ fontWeight: 600 }}>
                  Hola, {patientName || "Paciente"}
                </Typography>
              </div>
            </div>
          </Paper>
    
          <Paper className={classes.contentPaper} elevation={2}>
            {type === "agendar" && <ScheduleView data={sessionData} />}
            {type === "reagendar" && <RescheduleView data={sessionData} />}
            {type === "ver-citas" && <HistoryView data={sessionData} />}
            
            {!["agendar", "reagendar", "ver-citas"].includes(type) && (
                <Typography align="center">Acción desconocida.</Typography>
            )}
          </Paper>
    
          <Box mt={4}>
            <Typography variant="caption" color="textSecondary">
              Powered by ClinicChat
            </Typography>
          </Box>
        </div>
      );
  };

  return (
    <ThemeProvider theme={lightTheme}>
        {renderContent()}
    </ThemeProvider>
  );
};

export default PublicAccess;